import cn from 'classnames';
import React, { ElementType } from 'react';
import { createRoot } from 'react-dom/client';
import { invoke, merge, set } from 'lodash';
import ContentEditable from 'react-contenteditable';
import { connect, ConnectedProps } from 'react-redux';

import { location } from 'common/utils/url-helper';
import { analytics } from 'common/utils/analytics';
import { isLayerType } from 'common/utils/blocks/is-layer-type';
import { CardFacade, StoryFacade } from 'common/utils/facades';
import { pastePlainText, sleep, triggerFocus } from 'common/utils/helpers';
import { CARD_TYPE, COMPONENT_STATES, COMPONENT_TYPE, PARAMS } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';

import { StoryHistory } from 'client/utils';
import type { ClientReducerState } from 'client/reducers';
import type { AnswerContent, AnswerDataMap } from 'client/reducers/user/types';
import {
	getCardVotes,
	sendForm,
	getChallenge,
	sendAnswer,
	initUploadFile,
	sendSortableAnswer,
} from 'client/actions/api';
import { selectCard } from 'client/reducers/card/selectors';
import { updateAnswers } from 'client/actions/update-answers';
import { updateAnswerAttempts } from 'client/actions/update-answer-attempts';
import { DIAL_CODE_DATA_ATTR, phoneToE164 } from 'client/components/common/PhoneInput/common';
import { setFormData, SetFormDataPayload } from 'client/actions/set-form-data';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import { selectCmsItems, selectStory, selectStoryCardSettings } from 'client/reducers/story/selectors';
import { ScriptRenderer } from 'client/components/common/ScriptRenderer/ScriptRenderer';
import { selectTotalAnswersByCard, selectUserAnswersByCard } from 'client/reducers/user/selectors';
import { BBCommonProps, BBEditableModeProps, CardData, EditorMode, StoryModel, StorySettingsOfCard } from 'types/story';
import {
	EventProviderContextT,
	withEventProvider,
	type EventProviderFlowOptions,
} from 'client/components/pages/Story/EventProvider/Context';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { withFormContext } from 'client/components/common/StoryCard/Form/withFormContext';
import { FIELD_DATA_ATTR, FieldBBType } from 'client/components/common/BuildingBlocks/Fields/constants';
import { TimerEvEm } from 'client/components/common/BuildingBlocks/Timer/utils';
import { LottieEvEm } from 'client/components/common/BuildingBlocks/Lottie/utils';
import { AnimationEvEm } from 'client/components/common/BuildingBlocks/animation';
import type { FormDataT } from 'client/components/common/StoryCard/Form/types';
import cms from 'utils/cms';
import { generateNodeId } from 'src/common/utils/generate-id';
import { MASK_CHAR } from 'client/components/common/BuildingBlocks/AnswerInput/LetterModeInput';
import { CHILDREN_KEY, getElementIdByNodeId, getLinkData, getLinkProps, scrollIntoViewById } from '../utils/common';

import { DEFAULT_NAV_TIMEOUT } from './constants';
import traverseTreeDataCollector from '../utils/traverse-tree-data-collector';
import { AnswerInputMode } from '../AnswerInput/constants';

import css from './Button.scss';
import { uploadFormFiles } from '../Fields/FileUploadField/file-upload-to-bucket';

const { isPreview } = location.client;

const connector = connect(
	(state: ClientReducerState) => {
		const story = selectStory(state);
		const SF = new StoryFacade(story as StoryModel);
		const card = selectCard(state) as CardData;
		const CF = new CardFacade(card);
		const showResults = CF.getShowResults(SF.settings);
		const storyCardSettings = selectStoryCardSettings(state, CF.cardId);

		return {
			cardSettings: storyCardSettings,
			storyId: SF.storyId,
			cardType: CF.type,
			cardId: CF.cardId,
			storyName: SF.name,
			cardName: CF.card.data.name,
			userAnswers: selectUserAnswersByCard(state, CF.cardId),
			answersMin: CardFacade.getAnswersMin(CF.type, storyCardSettings),
			answersMax: CardFacade.getAnswersMax(CF.type, storyCardSettings),
			answersSelected: selectTotalAnswersByCard(state),
			forceSubmitBtn: CF.getForceSubmitButton(SF.settings),
			integrationsUrlParams: SF.settings.integrations?.urlParams?.params,
			showResults,
			cmsItems: selectCmsItems(state),
		};
	},
	{
		sendForm,
		getCardVotes,
		updateAnswers,
		updateAnswerAttempts,
		sendAnswer,
		initUploadFile,
		getChallenge,
		sendSortableAnswer,
		setFormData,
	}
);

type State = {
	html: string;
	editing: boolean;
	submitted: boolean;
};

type OwnProps = Exclude<BBCommonProps, 'children'> & { className?: string; children: string };
type ReduxProps = ConnectedProps<typeof connector>;
type Props = OwnProps & ReduxProps & EventProviderContextT;

const toPlainText = (v: string) => v?.replace(/<[^>]*>?/gm, '');

class Button extends React.Component<Props, State> {
	editableRef = React.createRef<HTMLElement>();

	isClicked = false;

	state = {
		html: this.props.children as string,
		editing: false,
		submitted: false,
	};

	componentDidUpdate = (prevProps: Props) => {
		const { cardId } = this.props;

		if (cardId !== prevProps.cardId && isLayerType(this.props).global) {
			this.isClicked = false;
		}

		const isChildrenChanged = prevProps.children !== this.props.children;
		const isPlatformChanged = prevProps.currentMediaQuery !== this.props.currentMediaQuery;
		if ((isChildrenChanged || isPlatformChanged) && this.props.children !== this.state.html) {
			this.setState({ html: this.props.children });
		}
	};

	onEditableBlur = event => {
		const { path } = event.target.dataset;
		const { currentMediaQuery } = this.props;

		// NOTE: children is always writes to defaultState, but for certain media query
		transmitToAdminBbUpdate({
			id: Button.name,
			path: `elements.${path}.${CHILDREN_KEY}.${COMPONENT_STATES.DEFAULT}.${currentMediaQuery}`,
			value: this.state.html,
			isStory: isLayerType(this.props).global,
		});

		this.setState({ editing: false });
	};

	onEditableChange = event => {
		this.setState({ html: toPlainText(event.target.value) });
		// trigger SelectionHint update to set current editable node sizes
		triggerFocus(this.editableRef.current);
	};

	onEditableDoubleClick = () => {
		this.setState({ editing: true }, () => {
			// update SelectionHint and place cursor into contenteditable block
			triggerFocus(this.editableRef.current);
		});
	};

	get eventListeners() {
		if (this.props.isEditableMode) {
			return {};
		}

		return {
			onClick: async (event: React.MouseEvent<HTMLLinkElement | HTMLButtonElement>) => {
				const _this = this;
				const { storyId, cardId, uiConfig } = this.props;
				const { internalLink, btnCollectionLink, externalLink, scrollTo } = getLinkData({ uiConfig, cardId });
				const flowEventOptions: EventProviderFlowOptions = {};

				if (location.client.isPreview && externalLink && !uiConfig.componentProps.btnTarget) {
					event.preventDefault();
				}

				if (this.isClicked && !scrollTo.currentPageBlockId) {
					return;
				}

				this.isClicked = true;

				analytics.onLinkClick({
					storyId,
					cardId,
					name: uiConfig.editorProps.name,
				});

				if (uiConfig.componentProps.gaEvent) {
					analytics.onCustomEvent({
						gaParamsStr: uiConfig.componentProps.gaEvent,
						storyName: this.props.storyName,
						cardName: this.props.cardName,
						linkUrl: this.props.uiConfig.componentProps.btnLink,
					});
				}

				invoke(this.props.eventListeners, 'onClick', event);

				if (scrollTo.currentPageBlockId) {
					scrollIntoViewById(scrollTo.currentPageBlockId, { hash: true });
					return;
				}

				if (event.currentTarget.tagName === 'A' && !internalLink) {
					// it is external link, any event shouldn't be called
					return;
				}

				const handleFormSubmit = async (): Promise<boolean> => {
					type FieldType = HTMLInputElement | HTMLTextAreaElement;
					const nodes = document.querySelectorAll<FieldType>(`[${FIELD_DATA_ATTR}]`);
					const fields = [...nodes].filter(isVisible);
					const formData = getFormData(fields, this.props.cardSettings ?? {});
					const { isValid } = await this.props.formContext?.validate?.(formData)!;

					if (!isValid) {
						this.isClicked = false;
						return false;
					}

					// files upload logic start

					const fileItems = Object.entries(formData)
						.filter(([_, item]) => item.type === 'file' && item.file)
						.map(([id, item]) => ({ id, ...item }));

					if (fileItems.length && !isPreview) {
						let uploadedItems: Awaited<ReturnType<typeof uploadFormFiles>>;

						this.props.formContext?.addToUploadQueue(fileItems[0].id);

						try {
							uploadedItems = await uploadFormFiles(fileItems, {
								storyId,
								cardId,
								getChallengeFn: this.props.getChallenge,
								initUploadFileFn: this.props.initUploadFile,
								setFieldError: this.props.formContext?.setFieldError,
							});
							// todo: handle upload errors to show error messages (mb GenericError)
						} catch (error) {
							console.error('File upload failed:', error);
							this.isClicked = false;
							return false;
						} finally {
							this.props.formContext?.removeFromUploadQueue(fileItems[0].id);
						}

						uploadedItems.forEach(item => {
							formData[item.id].data = item.data;

							delete formData[item.id].file;
						});
					}

					// files upload logic end

					TimerEvEm.emit('pause', { cardId });
					LottieEvEm.emit('playback', { action: 'onFormSubmit' });
					AnimationEvEm.emit('playback', { action: 'onFormSubmit' });

					try {
						this.setState({ submitted: true });
						// store form data in DB
						this.props.sendForm({
							storyId,
							cardId,
							formData: Object.values(formData),
						});

						const data = Object.entries(formData).reduce(
							(acc, [fieldId, value]) => {
								acc[fieldId] = value.data;
								return acc;
							},
							{} as SetFormDataPayload['data']
						);

						// store form data in redux store
						this.props.setFormData({ cardId, data });

						// collect submitted form data in local storage
						const sHistory = new StoryHistory({ storyId });
						sHistory.addFormData({ cardId, data });
					} catch {
						this.setState({ submitted: false });
					}

					return true;
				};

				const handleSortablePollSubmit = async (): Promise<boolean> => {
					if (!this.props.userAnswers) {
						return false;
					}
					TimerEvEm.emit('pause', { cardId });
					LottieEvEm.emit('playback', { action: 'onMultipleAnswerSubmit' });
					AnimationEvEm.emit('playback', { action: 'onMultipleAnswerSubmit' });

					const { selected } = this.props.userAnswers;
					this.setState({ submitted: true });

					try {
						// submit selected answers to backend
						if (!isPreview)
							this.props.sendSortableAnswer({ type: 'complete', cardId, storyId, items: selected });

						// Store submit in redux
						this.props.updateAnswers({ cardId, submitted: true });
						// collect submitted answer in extra storage
						const sHistory = new StoryHistory({ storyId });
						sHistory.addAnswer({ cardId, selectedAnswers: selected.map(id => ({ id })) });
					} catch {
						this.setState({ submitted: false });
					}

					return true;
				};

				const handleWithSubmitRequired = async (isMultipleAnswer: boolean): Promise<boolean> => {
					if (!this.props.userAnswers) {
						return false;
					}
					const selectedAnswersData = this.props.userAnswers.data || {};
					const selectedAnswerIds = this.props.userAnswers.selected.slice(0, this.props.answersMax);
					const selectedAnswers = selectedAnswerIds.reduce((acc, answerId) => {
						acc[answerId] = { customGAEvent: null, ...selectedAnswersData?.[answerId] };
						return acc;
					}, {} as Partial<AnswerDataMap>);

					// Stop timer component
					TimerEvEm.emit('pause', { cardId });
					LottieEvEm.emit('playback', { action: 'onMultipleAnswerSubmit' });
					AnimationEvEm.emit('playback', { action: 'onMultipleAnswerSubmit' });

					// Store votes and submit in redux
					this.props.updateAnswers({ cardId, submitted: true });

					// collect submitted answer in extra storage
					addAnswerToHistory({ storyId, cardId, selectedAnswers });

					// render pixels for selected answers
					renderAnswerPixels({ selectedAnswers, cardId });

					// submit selected answers to backend and get user votes
					// let votes: NonNullable<Props['userAnswers']>['votes'];
					try {
						if (!isPreview) {
							this.props.sendAnswer(
								createAnswerSubmission({
									storyId,
									cardId,
									selectedAnswerIds,
									selectedAnswersData,
									isMultipleAnswer,
								})
							);

							// Update votes after marking the chosen answer, as this operation might be time-consuming
							if (this.props.showResults) {
								const votes = await this.props.getCardVotes({ storyId, cardId });
								if (votes) this.props.updateAnswers({ cardId, votes });
							}
						}
						this.setState({ submitted: true });
					} catch {
						this.setState({ submitted: false });
					}

					return true;
				};

				/**
				 * Submit answer input field only (other answers are not considered)
				 */
				const handleSubmitAnswerInput = async (): Promise<boolean> => {
					if (!this.props.userAnswers) {
						return false;
					}

					const { data: selectedAnswersData = {} } = this.props.userAnswers;
					const selectedAnswers: Partial<AnswerDataMap> = {};
					let answerId: string | null = null;

					Object.entries(selectedAnswersData).forEach(([id, data]) => {
						if (data && data.type === 'answerInput') {
							selectedAnswers[id] = { ...data };
							answerId = id;
						}
					});

					if (!answerId) {
						return false;
					}

					// collect submitted answer in extra storage
					addAnswerToHistory({ storyId, cardId, selectedAnswers });

					// render pixels for selected answers
					renderAnswerPixels({ selectedAnswers, cardId });

					const { attempts, maxAttempts, isCorrect } = await this.props.updateAnswerAttempts({
						cardId,
						answerId,
					});
					const isComplete = attempts >= maxAttempts || isCorrect;

					flowEventOptions.isCorrect = isCorrect;

					// Store votes and submit in redux
					this.props.updateAnswers({ cardId, submitted: isComplete });

					const answerNodeId = generateNodeId(answerId, COMPONENT_TYPE.ANSWER_INPUT);
					LottieEvEm.emit('playback', { action: 'onAnswer', answerNodeId, isCorrect });
					AnimationEvEm.emit('playback', { action: 'onAnswer', answerNodeId, isCorrect });

					// submit selected answers to backend and get user votes
					try {
						if (!isPreview && isComplete) {
							this.props.sendAnswer(
								createAnswerSubmission({
									storyId,
									cardId,
									selectedAnswerIds: [answerId],
									selectedAnswersData,
									isMultipleAnswer: false,
								})
							);
						}
					} catch {
						console.error('Failed to submit answer input', { storyId, cardId, selectedAnswersData });
					}

					if (isComplete) {
						TimerEvEm.emit('pause', { cardId });
						LottieEvEm.emit('playback', { action: 'onMultipleAnswerSubmit' });
						AnimationEvEm.emit('playback', { action: 'onMultipleAnswerSubmit' });
						this.setState({ submitted: true });
						return true;
					}

					// allow to click again if there are attempts left
					this.isClicked = false;
					return false;
				};

				const handlers: Array<{
					check: (props: Props) => boolean;
					handler: () => Promise<boolean>;
					navTimeout: boolean;
				}> = [
					{ check: isFormSubmit, handler: handleFormSubmit, navTimeout: true },
					{ check: isSortablePollSubmit, handler: handleSortablePollSubmit, navTimeout: true },
					// Only POLL with `answersMinMax: [number, number]`
					{ check: isMultipleAnswerSubmit, handler: () => handleWithSubmitRequired(true), navTimeout: true },
					// Only POLL with `forceSubmitBtn: true`
					{ check: isForceSubmit, handler: () => handleWithSubmitRequired(false), navTimeout: true },
					// Only TRIVIA or POLL with answer input field
					{
						check: () => isAnswerInputSubmit(this.props),
						handler: () => handleSubmitAnswerInput(),
						navTimeout: true,
					},
					{ check: isSingleAnswerSubmit, handler: async () => false, navTimeout: true },
				];

				const matchedHandler = handlers.find(({ check }) => check(this.props));

				if (matchedHandler) {
					const success = await matchedHandler.handler();
					if (!success) return;
				}

				const defaultTimeout = matchedHandler?.navTimeout ? DEFAULT_NAV_TIMEOUT : 0;
				const timeoutProp = parseFloat(`${uiConfig.componentProps.answerTimeout}`);
				const redirectTimeout = (Number.isNaN(timeoutProp) ? defaultTimeout : timeoutProp) * 1000;
				await sleep(redirectTimeout);

				flow();

				function flow() {
					const searchParams: Record<string, string> = {};
					const isCollectionReference = cms.isCollectionReference(btnCollectionLink);

					if (isCollectionReference) {
						searchParams[PARAMS.COLLECTION_ITEM_ID] = cms.resolveCmsReferenceValue(
							btnCollectionLink,
							_this.props.cmsItems
						);
					}

					_this.props.eventProvider.events.flow(internalLink || _this.props._id, {
						isDirectId: !!internalLink,
						scrollTo: scrollTo.anotherPageBlockId,
						searchParams,
						...flowEventOptions,
					});
				}
			},
		};
	}

	/**
	 * Indicates that the button is disabled and cannot be clicked anymore.
	 */
	get isDisabled() {
		if (isFormSubmit(this.props)) {
			return this.state.submitted || this.props.formContext?.isUploadingFiles;
		}

		if (
			isMultipleAnswerSubmit(this.props) ||
			isSortablePollSubmit(this.props) ||
			isForceSubmit(this.props) ||
			isAnswerInputSubmit(this.props)
		) {
			return this.props.userAnswers?.submitted;
		}

		return isSingleAnswerSubmit(this.props);
	}

	/**
	 * Indicates that the button is inactive (greyed out) and cannot be clicked due to the lack of required conditions,
	 * like not enough answers selected, etc.
	 */
	get isInactive() {
		if (isFormSubmit(this.props)) return this.props.formContext?.isUploadingFiles;
		if (isSortablePollSubmit(this.props)) return false;
		if (isForceSubmit(this.props)) return this.props.answersSelected < this.props.answersMin;
		if (isMultipleAnswerSubmit(this.props)) return this.props.answersSelected < this.props.answersMin;
		if (isAnswerInputSubmit(this.props)) {
			const { userAnswers } = this.props;
			const answerComponent = traverseTreeDataCollector.findComponentByType(COMPONENT_TYPE.ANSWER_INPUT);
			let hasAnswer = false;

			if (answerComponent?.uiConfig.componentProps.answerInputMode === AnswerInputMode.Letter) {
				const [answerId, answer] =
					(Object.entries(userAnswers?.data ?? {}).find(([id, a]) => Boolean(a?.textContent)) as
						| [string, AnswerContent]
						| undefined) ?? [];

				const answerSettings = this.props.cardSettings?.answers?.[answerId ?? ''] as
					| (NonNullable<StorySettingsOfCard['answers']>[string] & { correctValue?: string })
					| undefined;

				const answerText = answer?.textContent?.replaceAll(MASK_CHAR, '').trim();

				hasAnswer =
					Boolean(answerText) &&
					answerText?.length === answerSettings?.correctValue?.split(',')[0].trim().length;
			} else {
				hasAnswer = Object.values(userAnswers?.data ?? {}).some(answer => Boolean(answer?.textContent));
			}

			return !hasAnswer;
		}
		return isSingleAnswerSubmit(this.props);
	}

	/**
	 * Indicates the end of the game, when the submit button on the last card is no longer needed and should be hidden.
	 */
	get isHidden() {
		const isLastCard = !this.props.eventProvider.nextDefaultCardId;
		return isLastCard && this.props.type === COMPONENT_TYPE.BUTTON_SUBMIT && !!this.props.userAnswers?.submitted;
	}

	get resolvedHtml(): string {
		return cms.resolveCmsReferenceValue(this.state.html, this.props.cmsItems);
	}

	renderEditable() {
		const { editableModeProps, uiConfig } = this.props;
		const nodeProps = merge({}, uiConfig.nodeProps, editableModeProps?.nodeProps);
		const isContentMode = this.props.editorMode === EditorMode.CONTENT;

		/**
		 * Why do we assign "updater" to className?
		 * This is a temporary fix, to update <ContentEditable> in case of
		 * some state was provided via nodeProps. Otherwise <ContentEditable> won't be
		 * updated, because of his own shouldComponentUpdate logic, will return false
		 * if there were changed some data attributes only.
		 */
		let updater = '';
		const updaterObj = {
			...nodeProps,
			...this.props.stateAttrs,
			...(isContentMode ? { 'data-content-mode': true } : null),
		};
		Object.keys(updaterObj).forEach(k => {
			const res = k.match(/^(data-)/);
			if (res) updater += `${k}=${updaterObj[k]}`;
		});

		return (
			<ContentEditable
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				innerRef={this.editableRef}
				className={cn(css.btn, css.editable, uiConfig.nodeProps.className, updater)}
				tagName="div"
				html={this.resolvedHtml}
				disabled={!this.state.editing}
				onDoubleClick={this.onEditableDoubleClick}
				onChange={this.onEditableChange}
				onBlur={this.onEditableBlur}
				onPaste={pastePlainText}
				{...(isContentMode
					? {
							onClick: e => {
								this.props.eventListeners?.onClick?.(e);
								this.onEditableDoubleClick();
							},
						}
					: null)}
			/>
		);
	}

	renderDefault(props: Partial<BBEditableModeProps['nodeProps']> = {}) {
		const { isInactive, isDisabled, isHidden } = this;
		const { nodeProps } = this.props.uiConfig;
		const { Component, ...componentProps } = getComponent(this.props);

		return (
			<Component
				{...componentProps}
				{...nodeProps}
				{...this.props.stateAttrs}
				{...this.props.eventListeners}
				{...this.eventListeners}
				{...props}
				style={{ ...nodeProps.style, ...props.style, ...(isHidden ? { visibility: 'hidden' } : null) }}
				className={cn(css.btn, nodeProps.className, props.className, {
					[css.inactive]: isInactive,
				})}
				/* eslint-disable-next-line react/no-danger */
				dangerouslySetInnerHTML={{ __html: this.resolvedHtml }}
				ref={this.props.containerRef}
				disabled={isDisabled || isInactive}
			/>
		);
	}

	render() {
		return this.props.isEditableMode ? this.renderEditable() : this.renderDefault();
	}
}

interface AnswerSubmissionParams {
	storyId: string;
	cardId: string;
	selectedAnswerIds: string[];
	selectedAnswersData: Partial<AnswerDataMap>;
	isMultipleAnswer: boolean;
}

const createAnswerSubmission = ({
	storyId,
	cardId,
	selectedAnswerIds,
	selectedAnswersData,
	isMultipleAnswer,
}: AnswerSubmissionParams): Parameters<typeof sendAnswer>[0] => {
	const getAnswerText = (id: string) => selectedAnswersData?.[id]?.textContent ?? '';
	const getAnswerGaEvent = (id: string) => selectedAnswersData?.[id]?.customGAEvent || null;

	const baseSubmission = {
		storyId,
		cardId,
	};

	if (isMultipleAnswer) {
		return {
			...baseSubmission,
			answerIds: selectedAnswerIds,
			answerText: selectedAnswerIds.map(getAnswerText),
			customGAEvent: selectedAnswerIds.map(getAnswerGaEvent),
		};
	}

	const answerId = selectedAnswerIds[0];
	return {
		...baseSubmission,
		answerId,
		answerText: getAnswerText(answerId),
		customGAEvent: getAnswerGaEvent(answerId),
	};
};

export function isFormSubmit(props: Pick<Props, 'uiConfig' | 'cardType'>) {
	// check UNIFORM_PROPS.btnSubmit instead of component.type to support old stories
	return !!props.uiConfig.componentProps[UNIFORM_PROPS.btnSubmit] && props.cardType === CARD_TYPE.FORM;
}

export function isSortablePollSubmit(props: Pick<Props, 'cardType' | 'type'>) {
	return props.type === COMPONENT_TYPE.BUTTON_SUBMIT && props.cardType === CARD_TYPE.SORTABLE_POLL;
}

export function isAnswerSubmit(props: Pick<Props, 'cardType' | 'type'>) {
	return props.type === COMPONENT_TYPE.BUTTON_SUBMIT && CardFacade.hasFeature(props.cardType).answer;
}

export function isMultipleAnswerSubmit(props: Pick<Props, 'cardType' | 'answersMax' | 'type'>) {
	return isAnswerSubmit(props) && props.answersMax > 1;
}

export function isSingleAnswerSubmit(props: Pick<Props, 'cardType' | 'answersMax' | 'type'>) {
	return isAnswerSubmit(props) && props.answersMax === 1;
}

export function isForceSubmit(props: Pick<Props, 'cardType' | 'answersMax' | 'type' | 'forceSubmitBtn'>) {
	return isAnswerSubmit(props) && props.forceSubmitBtn;
}

function isAnswerInputSubmit(props: Props) {
	return isAnswerSubmit(props) && !!traverseTreeDataCollector.findComponentByType(COMPONENT_TYPE.ANSWER_INPUT);
}

function isVisible(e: HTMLInputElement | HTMLTextAreaElement) {
	// reject visually hidden fields (no size, display: none, etc.)
	return Boolean(e.offsetWidth || e.offsetHeight || e.getClientRects().length);
}

function getComponent(props: Props) {
	const externalLink = cms.resolveCmsReferenceValue(
		props.uiConfig.componentProps[UNIFORM_PROPS.btnLink],
		props.cmsItems
	);

	if (externalLink && !isFormSubmit(props)) {
		return getLinkProps(props.uiConfig, { link: externalLink, integrationUrlParams: props.integrationsUrlParams });
	}

	return {
		Component: 'button' as ElementType,
		type: 'button',
	};
}

interface RenderPixelsParams {
	selectedAnswers: Partial<AnswerDataMap>;
	cardId: string;
}

export function renderAnswerPixels({ selectedAnswers, cardId }: RenderPixelsParams) {
	if (!selectedAnswers || !cardId) {
		return;
	}

	Object.entries(selectedAnswers).forEach(([answerId, data]) => {
		// render pixels for each answer
		if (!data?.embedCode) return;

		const container = document.createElement('div');
		container.id = `answer-script-${cardId}-${answerId}`;
		document.body.appendChild(container);

		const root = createRoot(container);
		root.render(<ScriptRenderer value={data.embedCode} scriptOnly />);
	});
}

interface AddAnswerToHistoryParams {
	storyId: string;
	cardId: string;
	selectedAnswers: Partial<AnswerDataMap>;
}

export function addAnswerToHistory({ storyId, cardId, selectedAnswers }: AddAnswerToHistoryParams) {
	const sHistory = new StoryHistory({ storyId });

	// collect submitted answer in extra storage
	sHistory.addAnswer({
		cardId,
		selectedAnswers: Object.keys(selectedAnswers).map(id => ({ id })),
	});
}

function getFormData(fields: (HTMLInputElement | HTMLTextAreaElement)[], cardSettings: StorySettingsOfCard) {
	return fields.reduce<FormDataT>((memo, field) => {
		const { value } = field;
		const file = field instanceof HTMLInputElement && field.type === 'file' ? field.files?.[0] : null;

		const type =
			field.type === FieldBBType.hidden
				? field.getAttribute('data-field-type') || (field as HTMLInputElement).dataset?.fieldType
				: field.type;

		const checked = 'checked' in field ? field.checked : undefined;

		const nodeId = field.dataset.bb ? field.id : field.closest('[data-bb]')?.id;

		const id = getElementIdByNodeId(nodeId ?? '') ?? '';
		const name = cardSettings.input?.[id]?.name || field.name;
		const dialCode = field.getAttribute(DIAL_CODE_DATA_ATTR) ?? '';

		const data =
			file instanceof File
				? 'file'
				: type === 'tel'
					? phoneToE164(
							!value ? '' : value.startsWith('+') ? value : dialCode ? `+${dialCode}${value}` : value
						)
					: value;

		set(memo, id, {
			name,
			type,
			data,
			...(file && { file }),
			attributes: {
				id,
				...(type === 'checkbox' || type === 'radio' ? { checked } : null),
			},
		});

		return memo;
	}, {});
}

const WithRedux = connector(Button);
const WithFormContext = withFormContext(WithRedux);
const WithCardTransitionContext = withCardTransitionContext(WithFormContext);

export default withEventProvider(WithCardTransitionContext);
